import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { countsFromGrades, majorityGrade } from './majorityJudgment';

// Type for grade descriptions (K2 - human-readable)
export type GradeDescriptions = Record<string, string>;

// Type for grade rankings (K3 - numerical rank for sorting)
export type GradeRankings = Record<string, number>;

// Pure grades only (no slashes) - used for test-retest resolution
export type PureGrade = 'A' | 'B' | 'C' | 'D' | 'F';

// Complete grade mapping interface
export interface GradeMapping {
  k1: string; // Pairing key (storage format, e.g., "A/B")
  k2: string; // Human-readable description (e.g., "approaching super intelligent")
  k3: number; // Numerical rank (1-15, for sorting/Balinski)
}

// Caches for grade data
let cachedDescriptions: GradeDescriptions | null = null;
let cachedRankings: GradeRankings | null = null;
let cachedMappings: Map<string, GradeMapping> | null = null;

/**
 * Fetch complete grade mappings from Firestore
 * Returns all three keys (K1, K2, K3) for each grade
 */
export async function getGradeMappings(): Promise<Map<string, GradeMapping>> {
  if (cachedMappings) {
    return cachedMappings;
  }

  try {
    const collectionRef = collection(db, 'majorityJudgmentWords');
    const querySnapshot = await getDocs(collectionRef);

    if (!querySnapshot.empty) {
      const mappings = new Map<string, GradeMapping>();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const k1 = data.k1 || data.grade; // Fallback to legacy field
        const k2 = data.k2 || data.description;
        const k3 = data.k3 || data.rank || 9; // Default to middle rank
        
        mappings.set(k1, { k1, k2, k3 });
      });
      
      cachedMappings = mappings;
      return cachedMappings;
    } else {
      console.warn('Grade mappings collection empty, using fallback');
      return getFallbackMappings();
    }
  } catch (error) {
    console.error('Error fetching grade mappings:', error);
    return getFallbackMappings();
  }
}

/**
 * Fetch grade descriptions (K2) from Firestore collection
 * Each grade is a separate document for easy updates
 * Results are cached to avoid repeated network calls
 */
export async function getGradeDescriptions(): Promise<GradeDescriptions> {
  // Return cached version if available
  if (cachedDescriptions) {
    return cachedDescriptions;
  }

  // Get from complete mappings
  const mappings = await getGradeMappings();
  const descriptions: GradeDescriptions = {};
  
  mappings.forEach((mapping, k1) => {
    descriptions[k1] = mapping.k2;
  });
  
  cachedDescriptions = descriptions;
  return cachedDescriptions;
}

/**
 * Fetch grade rankings (K3) from Firestore collection
 * Used for sorting and Balinski gauge algorithm
 */
export async function getGradeRankings(): Promise<GradeRankings> {
  if (cachedRankings) {
    return cachedRankings;
  }

  const mappings = await getGradeMappings();
  const rankings: GradeRankings = {};
  
  mappings.forEach((mapping, k1) => {
    rankings[k1] = mapping.k3;
  });
  
  cachedRankings = rankings;
  return cachedRankings;
}

/**
 * Fallback mappings if Firestore fetch fails
 * Complete 15-entry mapping system (K1, K2, K3)
 */
function getFallbackMappings(): Map<string, GradeMapping> {
  const mappings = new Map<string, GradeMapping>();
  
  const data: GradeMapping[] = [
    // Row A
    { k1: 'A',   k2: 'super intelligent',                      k3: 1 },
    { k1: 'A/B', k2: 'approaching super intelligent',          k3: 2 },
    { k1: 'A/C', k2: 'generally intelligent',                  k3: 3 },
    { k1: 'A/D', k2: 'approaching generally intelligent',      k3: 5 },
    { k1: 'A/F', k2: 'artificially intelligent',              k3: 7 },
    
    // Row B
    { k1: 'B',   k2: 'generally intelligent',                  k3: 4 },
    { k1: 'B/C', k2: 'approaching generally intelligent',      k3: 6 },
    { k1: 'B/D', k2: 'artificially intelligent',              k3: 8 },
    { k1: 'B/F', k2: 'approaching artificially intelligent',   k3: 10 },
    
    // Row C
    { k1: 'C',   k2: 'artificially intelligent',              k3: 9 },
    { k1: 'C/D', k2: 'approaching artificially intelligent',   k3: 11 },
    { k1: 'C/F', k2: 'unintelligent',                         k3: 12 },
    
    // Row D
    { k1: 'D',   k2: 'unintelligent',                         k3: 13 },
    { k1: 'D/F', k2: 'approaching unintelligent',             k3: 14 },
    
    // Row F
    { k1: 'F',   k2: 'failed',                                k3: 15 },
  ];
  
  data.forEach(mapping => {
    mappings.set(mapping.k1, mapping);
  });
  
  return mappings;
}

/**
 * Fallback descriptions if Firestore fetch fails (legacy)
 */
function getFallbackDescriptions(): GradeDescriptions {
  const mappings = getFallbackMappings();
  const descriptions: GradeDescriptions = {};
  
  mappings.forEach((mapping, k1) => {
    descriptions[k1] = mapping.k2;
  });
  
  return descriptions;
}

/**
 * Get description for a specific grade
 */
export async function getGradeDescription(grade: string): Promise<string> {
  const descriptions = await getGradeDescriptions();
  return descriptions[grade] || 'unknown';
}

/**
 * Get ranking (K3) for a specific grade
 * Used for sorting and Balinski gauge algorithm
 */
export async function getGradeRanking(grade: string): Promise<number> {
  const rankings = await getGradeRankings();
  return rankings[grade] || 9; // Default to middle rank
}

/**
 * Preload all grade data at app start for optimal performance
 * Call this once when the app initializes to avoid lazy loading delays
 */
export async function preloadGradeData(): Promise<void> {
  console.log('📊 Preloading grade data from Firestore...');
  try {
    await getGradeMappings(); // This will populate all three caches
    console.log('✅ Grade data preloaded successfully', {
      mappings: cachedMappings?.size || 0,
      descriptions: Object.keys(cachedDescriptions || {}).length,
      rankings: Object.keys(cachedRankings || {}).length
    });
  } catch (error) {
    console.error('❌ Failed to preload grade data, will use fallback:', error);
  }
}

/**
 * Check if grade data has been loaded
 */
export function isGradeDataLoaded(): boolean {
  return cachedMappings !== null && cachedRankings !== null;
}

/**
 * Clear all caches (useful for testing or forcing refresh)
 */
export function clearGradeDescriptionsCache(): void {
  cachedDescriptions = null;
  cachedRankings = null;
  cachedMappings = null;
}


/**
 * Resolve test-retest grades to a single pure grade
 * 
 * Takes array of grades (from progressive grading rounds) and returns
 * single letter grade (A, B, C, D, or F) for matrix display and ranking.
 * 
 * Algorithm:
 * 1. Sort grades by K3 rank (best to worst: A=1, B=2, C=3, D=4, F=5)
 * 2. Odd-count array: Return middle grade directly
 * 3. Even-count array: Look up middle pair in K2 table
 * 
 * Examples:
 *   ['D', 'B', 'A'] → Sort to ['A', 'B', 'D'] → Odd count (3) → Middle is 'B'
 *   ['A', 'C'] → Already sorted → Even count (2) → K2['A,C'] → 'B'
 *   ['A', 'B', 'C', 'D'] → Even count (4) → Middle pair is ['B', 'C'] → K2['B,C'] → 'B'
 * 
 * @param grades - Array of letter grades (can be in any order, may include duplicates)
 * @returns Single pure grade (A, B, C, D, or F)
 */
export function resolveTestRetestGrades(grades: string[]): PureGrade {
  // One grader's repeated grades collapse to their Balinski–Laraki majority
  // grade: the lower of the two middle grades when the count is even. The
  // previous pair table rounded an even split to its midpoint ("A,C" → B),
  // which is an average, not a majority grade, and contradicted how the
  // same grader's grades were stacked once aggregated.
  return majorityGrade(countsFromGrades(grades)) ?? 'C';
}
